import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  IntakeLabelsByProgram,
  requiredFieldsByProgram,
} from '@/components/signup_components/PartnerSignup/common/helperFunctions/intakeFunctions';
import useContact from '@/hooks/useContact';
import { ContactProps, ContactType, localizedTextEquals } from '@/models/Contact';
import { PartnerProps } from '@/models/Partner';
import { PartnerGroupProps } from '@/models/PartnerGroup';
import {
  PartnerSubCategoryApiResponse,
  ProgramApiResponse,
  ProgramNames,
  ProgramProps,
} from '@/models/Program';
import { countyIdIsRequired, stateIdIsRequired, validateContact } from '@/utils/contactUtils';

import { ConfigContext } from './ConfigContext';
import { LocaleContext } from './LocaleContext';
import { UsersContext } from './UsersContext';

interface Props {
  partnerInfo: PartnerProps;
  partnerGroupInfo: PartnerGroupProps;
  primaryContact: ContactProps | undefined;
  shippingContact: ContactProps | undefined;
  countryId: number | null;
  stateId: string | null;
  shippingSame: boolean;
  isNewPartner: boolean;
  showErrors: boolean;
  intakeHasErrors: boolean; // this is a flag to indicate if there are any errors in the intake form
  shippingContactFieldsInError: Array<keyof ContactProps>;
  primaryContactFieldsInError: Array<keyof ContactProps>;
  partnerFieldsInError: Array<keyof PartnerProps>;
  intakeLabels: { [key in keyof PartnerProps]: string };
  disabledNewPartnerCheckbox: boolean;
  selectedProgramInfo: ProgramProps;
  updatePartnerInfo: (partnerInfo: PartnerProps) => void;
  updatePartnerGroupInfo: (partnerGroupInfo: PartnerGroupProps) => void;
  updatePrimaryContact: (primaryContact: ContactProps) => void;
  updateShippingContact: (shippingContact: ContactProps) => void;
  updateCountryId: (countryId: number | null) => void;
  updateStateId: (stateId: string) => void;
  updateShippingSame: (shippingSame: boolean) => void;
  updateShowErrors: (showErrors: boolean) => void;
  updateIsNewPartner: (isNewPartner: boolean) => void;
  updateSelectedProgramId: (programId: ProgramNames | null) => void;
}

interface ProviderProps {
  children: React.ReactNode;
  disabledNewPartnerCheckbox?: boolean;
  partnerGroupId?: number | null;
  programId?: ProgramNames | null;
}

export const PartnerIntakeContext = createContext<Props>({
  partnerInfo: {},
  partnerGroupInfo: {},
  primaryContact: {},
  shippingContact: {},
  countryId: null,
  stateId: null,
  shippingSame: false,
  isNewPartner: false,
  showErrors: false,
  shippingContactFieldsInError: [],
  disabledNewPartnerCheckbox: false,
  intakeHasErrors: false,
  primaryContactFieldsInError: [],
  partnerFieldsInError: [],
  intakeLabels: {},
  selectedProgramInfo: {} as ProgramProps,
  updatePartnerInfo: () => {},
  updatePartnerGroupInfo: () => {},
  updatePrimaryContact: () => {},
  updateShippingContact: () => {},
  updateCountryId: () => {},
  updateStateId: () => {},
  updateShippingSame: () => {},
  updateShowErrors: () => {},
  updateIsNewPartner: () => {},
  updateSelectedProgramId: () => {},
});

export const PartnerIntakeProvider = ({
  children,
  disabledNewPartnerCheckbox = false,
  programId = null,
}: ProviderProps) => {
  const { translatedText, localeLanguageCode } = useContext(LocaleContext);
  const { contactTypes, allPrograms } = useContext(ConfigContext);
  const { userCurrent } = useContext(UsersContext);

  const { getContact } = useContact();

  const [primaryContact, setPrimaryContact] = useState<ContactProps>({
    email: userCurrent.email,
  } as ContactProps);
  const [shippingContact, setShippingContact] = useState<ContactProps | undefined>(undefined);
  const [selectedProgramId, setSelectedProgramId] = useState<ProgramNames | null>(programId);
  const [fetchedProgramInfo, setFetchedProgramInfo] = useState<ProgramProps | null>(null);

  useEffect(() => {
    setSelectedProgramId((prevValue) => {
      if (programId != prevValue) {
        return programId;
      }

      return prevValue;
    });
  }, [programId]);

  const [partnerInfo, setPartnerInfo] = useState<PartnerProps>({
    partnerSubCategoryId: null, //community category
    partnerName: null,
    primaryContactId: null,
    programId: Number(selectedProgramId),
    partnerSubCategoryOther: '',
    schoolDistrict: null,
    lowestGrade: null,
    highestGrade: null,
    countyId: null,
    stateId: null,
    countryId: null,
  });

  const [partnerGroupInfo, setPartnerGroupInfo] = useState<PartnerGroupProps>(
    userCurrent?.partnerGroup ?? ({})
  );

  const [countryId, setCountryId] = useState<number | null>(null);
  const [stateId, setStateId] = useState<string | null>(null);
  const [shippingSame, setShippingSame] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState(false);
  const [shippingContactFieldsInError, setShippingContactFieldsInError] = useState<
    Array<keyof ContactProps>
  >([]);
  const [primaryContactFieldsInError, setPrimaryContactFieldsInError] = useState<
    Array<keyof ContactProps>
  >([]);
  const [partnerFieldsInError, setPartnerFieldsInError] = useState<Array<keyof PartnerProps>>([]);

  const [isNewPartner, setIsNewPartner] = useState(disabledNewPartnerCheckbox);

  const partnerPrimaryContactType = useMemo(
    () =>
      contactTypes.find(
        (c: ContactType) =>
          localizedTextEquals(c.contactTypeName, 'PartnerPrimary') &&
          c.programId === selectedProgramId
      ),
    [contactTypes, selectedProgramId]
  );

  const shippingContactType = useMemo(
    () =>
      contactTypes.find(
        (c: ContactType) =>
          localizedTextEquals(c.contactTypeName, 'Shipping Address') &&
          c.programId === selectedProgramId
      ),
    [contactTypes, selectedProgramId]
  );

  const intakeHasErrors = useMemo(() => {
    return (
      primaryContactFieldsInError.length > 0 ||
      partnerFieldsInError.length > 0 ||
      (isNewPartner && shippingContactFieldsInError.length > 0)
    );
  }, [
    shippingContactFieldsInError.length,
    partnerFieldsInError.length,
    isNewPartner,
    primaryContactFieldsInError.length,
  ]);

  const intakeLabels = useMemo(() => {
    if (selectedProgramId != null) {
      return IntakeLabelsByProgram(
        selectedProgramId,
        fetchedProgramInfo?.partnerTerm ?? '',
        translatedText
      );
    }

    return {};
  }, [selectedProgramId, fetchedProgramInfo?.partnerTerm, translatedText]);

  const updateSelectedProgramId = useCallback((updatedProgramId: ProgramNames | null) => {
    setSelectedProgramId(updatedProgramId);
    if (updatedProgramId == null) {
      setFetchedProgramInfo(null);
    }
  }, []);

  useEffect(() => {
    if (allPrograms != null && selectedProgramId != null) {
      const selectedInfo = allPrograms.find(
        (element: ProgramApiResponse) => element.id === selectedProgramId
      );

      if (
        selectedInfo != null &&
        selectedInfo.partnerSubCategories != null &&
        localeLanguageCode != null
      ) {
        setFetchedProgramInfo({
          ...selectedInfo,
          partnerTerm:
            selectedInfo.partnerTerm[localeLanguageCode.length > 0 ? localeLanguageCode : 'en'],
          partnerSubCategories: selectedInfo.partnerSubCategories.map(
            (subCategory: PartnerSubCategoryApiResponse) =>
              // skip if subCategory is null
              subCategory?.subCategory != null
                ? {
                    ...subCategory,
                    subCategory:
                      subCategory.subCategory[
                        localeLanguageCode.length > 0 ? localeLanguageCode : 'en'
                      ],
                  }
                : null
          ),
        } as ProgramProps);
      }
    }
  }, [allPrograms, selectedProgramId, localeLanguageCode]);

  const updateShippingContact = useCallback((updatedContact: ContactProps) => {
    setShippingContact(updatedContact);
  }, []);

  const updateCountryId = useCallback((updatedCountryId: number | null) => {
    setCountryId(updatedCountryId);
  }, []);

  const updateStateId = useCallback((updatedStateId: string) => {
    setStateId(updatedStateId);
  }, []);

  const updateShippingSame = useCallback(
    (shippingSameVal: boolean) => {
      setShippingSame(shippingSameVal);

      if (shippingSameVal) {
        setShippingContact(primaryContact);
      } else {
        setShippingContact(undefined);
      }
    },
    [primaryContact]
  );

  const updateShowErrors = useCallback((showErrorsVal: boolean) => {
    setShowErrors(showErrorsVal);
  }, []);

  const updateIsNewPartner = useCallback((updatedPartnerCheck: boolean) => {
    setIsNewPartner(updatedPartnerCheck);
  }, []);

  useEffect(() => {
    const fetchShippingContact = async () => {
      if (partnerInfo?.shippingContactId != null) {
        const contact = await getContact(partnerInfo.shippingContactId);
        updateShippingContact(contact);
      }
    };

    void fetchShippingContact();
  }, [getContact, updateShippingContact, partnerInfo]);

  useEffect(() => {
    const fetchContact = async (contactId: number) => {
      const contact = await getContact(contactId);
      setPrimaryContact(contact);
    };

    if (userCurrent != null && userCurrent.contactId != null && primaryContact.id == null) {
      void fetchContact(userCurrent.contactId);
    }
  }, [getContact, userCurrent, primaryContact]);

  // temporary fix to update partnerInfo state, county, country based on shipping contact since we don't have a way to update them explictly in the intake form
  useEffect(() => {
    let infoToUpdate = {};

    if (shippingContact?.stateId != null && shippingContact.stateId != partnerInfo?.stateId) {
      infoToUpdate = {
        ...infoToUpdate,
        stateId: shippingContact.stateId,
      };
    }

    if (shippingContact?.countyId != null && shippingContact.countyId != partnerInfo?.countyId) {
      infoToUpdate = {
        ...infoToUpdate,
        countyId: shippingContact.countyId,
      };
    }

    if (
      selectedProgramId != ProgramNames.TreeCitiesWorld && // Users select a country for Tree Cities World so we don't want to override it with the partner's shipping country
      shippingContact?.countryId != null &&
      shippingContact.countryId != partnerInfo?.countryId
    ) {
      infoToUpdate = {
        ...infoToUpdate,
        countryId: shippingContact.countryId,
      };
    }

    if (Object.keys(infoToUpdate).length > 0) {
      setPartnerInfo((prevValue) => ({
        ...prevValue,
        ...infoToUpdate,
      }));
    }
  }, [
    shippingContact?.stateId,
    shippingContact?.countyId,
    shippingContact?.countryId,
    partnerInfo,
    selectedProgramId,
    setPartnerInfo,
  ]);

  useEffect(() => {
    if (partnerPrimaryContactType != null && primaryContact != null) {
      const contactFields: Array<keyof ContactProps> = validateContact(
        primaryContact,
        partnerPrimaryContactType
      );

      setPrimaryContactFieldsInError(contactFields);
    }
  }, [primaryContact, selectedProgramId, partnerPrimaryContactType]);

  useEffect(() => {
    if (shippingContactType != null && shippingContact != null) {
      const contactFields: Array<keyof ContactProps> = validateContact(
        shippingContact,
        shippingContactType
      );

      setShippingContactFieldsInError(contactFields);
    }
  }, [shippingContact, selectedProgramId, shippingContactType, isNewPartner]);

  useEffect(() => {
    const pf: Array<keyof PartnerProps> = [];
    if (selectedProgramId == null) return;

    if (isNewPartner) {
      Object.keys(partnerInfo).forEach((key: string) => {
        const partnerInfoKey = key as keyof PartnerProps;

        const value = partnerInfo[partnerInfoKey];

        if (value == null || value === '' || value === 0)
          if (!requiredFieldsByProgram(selectedProgramId).includes(partnerInfoKey)) {
            return;
          } else if (
            partnerInfoKey === ('stateId' as keyof PartnerProps) &&
            partnerInfo.countryId != null &&
            !stateIdIsRequired(selectedProgramId, partnerInfo.countryId)
          ) {
            return;
          } else if (
            partnerInfoKey === ('stateId' as keyof PartnerProps) &&
            partnerInfo.countryId != null &&
            !countyIdIsRequired(selectedProgramId, partnerInfo.countryId)
          ) {
            return;
          } else if (partnerInfoKey === ('lowestGrade' as keyof PartnerProps)) {
            if (partnerInfo.lowestGrade === 0) return; // 0 is a valid value for lowest grade

            pf.push('lowestGrade');
          } else if (partnerInfoKey === ('highestGrade' as keyof PartnerProps)) {
            if (partnerInfo.highestGrade === 0) return; // 0 is a valid value for highest grade

            pf.push('highestGrade');
          } else if (partnerInfoKey === ('partnerSubCategoryOther' as keyof PartnerProps)) {
            const selectedPartnerSubCategory = fetchedProgramInfo?.partnerSubCategories.find(
              (c) => c.id == partnerInfo.partnerSubCategoryId
            );
            const isOtherCategory =
              selectedPartnerSubCategory?.subCategory?.toString().toLowerCase() === 'other';

            if (!isOtherCategory) {
              return;
            }

            if (isOtherCategory && partnerInfo.partnerSubCategoryOther == null) {
              pf.push('partnerSubCategoryId');
            }
          } else {
            pf.push(partnerInfoKey);
          }
      });

      if (partnerInfo.schoolDistrict && partnerGroupInfo?.partnerName == null) {
        pf.push('schoolDistrict');
      }
    } else if (!isNewPartner) {
      if (partnerInfo?.id == null) {
        pf.push('partnerId' as keyof PartnerProps);
      }
    }

    setPartnerFieldsInError(pf);
  }, [
    isNewPartner,
    partnerGroupInfo?.partnerName,
    partnerInfo,
    selectedProgramId,
    fetchedProgramInfo?.partnerSubCategories,
  ]);

  const intakeLoading = fetchedProgramInfo == null;

  const selectedProgramInfo = fetchedProgramInfo!;

  return (
    <PartnerIntakeContext.Provider
      value={{
        partnerInfo,
        partnerGroupInfo,
        primaryContact,
        shippingContact,
        countryId,
        stateId,
        shippingSame,
        isNewPartner,
        showErrors,
        intakeHasErrors,
        shippingContactFieldsInError,
        disabledNewPartnerCheckbox,
        primaryContactFieldsInError,
        partnerFieldsInError,
        intakeLabels,
        selectedProgramInfo,
        updatePartnerInfo: setPartnerInfo,
        updatePartnerGroupInfo: setPartnerGroupInfo,
        updatePrimaryContact: setPrimaryContact,
        updateShippingContact,
        updateCountryId,
        updateStateId,
        updateShippingSame,
        updateShowErrors,
        updateIsNewPartner,
        updateSelectedProgramId,
      }}
    >
      {!intakeLoading && children}
    </PartnerIntakeContext.Provider>
  );
};
